import { useMemo, useState } from 'react';
import {
  DndContext, DragEndEvent, DragOverlay, DragStartEvent,
  MouseSensor, TouchSensor, pointerWithin, rectIntersection,
  CollisionDetection, useSensor, useSensors
} from '@dnd-kit/core';
import { useEstadosLote, useFases } from '@/hooks/useFases';
import { useChangeEstadoLote, useLotesKanban } from '@/hooks/useLotesKanban';
import { LotesKanbanColumn } from './LotesKanbanColumn';
import { LoteCard } from './LoteCard';
import { EstadoPickerDialog } from './EstadoPickerDialog';
import type { ProyectoCode } from '@/types/sitePlan';
import type { EstadoLote, Fase, LoteKanban } from '@/types';

interface Props {
  proyecto: ProyectoCode;
  search?: string;
  // Filtro opcional por IDCol del creador APC. Si null, no filtra.
  creadorApcId?: number | null;
}

// Nuestra empresa solo hace 'Permiso Vivienda'. El catalogo trae tambien
// estados de 'Otros' (APC EN - Edificaciones Nacionales) pero los ocultamos
// del UI. Quedan en la DB por si en un futuro cambian las prioridades del
// negocio; para reactivar APC EN alcanza con relajar este filtro.
const TIPO_PROYECTO_ACTIVO = 'Permiso Vivienda';

// Color de la banda superior por etapa. Las etapas no son entidades en la
// DB asi que el mapeo vive aca. Cada etapa tiene su familia de color y
// las columnas de fase debajo van en gradiente ascendente hacia el
// "Permiso de Construccion" (verde profundo, objetivo final).
const ETAPA_COLORS: Record<string, string> = {
  Diseno: '#5D636C',
  'APC CFIA': '#2563EB',
  'APC Institucional': '#D97706',
  'APC Municipal': '#059669',
};

function etapaColor(etapa: string): string {
  return ETAPA_COLORS[etapa] ?? '#5D636C';
}

// pointerWithin + rectIntersection: el primero detecta colisiones donde
// esta el puntero (clave para soltar en columnas vacias), el segundo
// rellena cuando el puntero queda fuera del area droppable durante el
// gesto. Misma estrategia que el KanbanBoard de tareas — ver leccion
// "dnd-kit collision detection on empty containers" en CLAUDE.md.
const collisionDetection: CollisionDetection = (args) => {
  const pointer = pointerWithin(args);
  return pointer.length > 0 ? pointer : rectIntersection(args);
};

function agrupar(lotes: LoteKanban[], fases: Fase[]): Map<number, LoteKanban[]> {
  const sinTramitar = fases.find(f => f.nombre.toLowerCase().startsWith('sin tramitar'));
  const fallbackId = sinTramitar?.idFase ?? 0;
  const map = new Map<number, LoteKanban[]>();
  for (const l of lotes) {
    const key = l.idFaseLote ?? fallbackId;
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(l);
  }
  return map;
}

function matchSearch(l: LoteKanban, needle: string): boolean {
  if (!needle) return true;
  const n = needle.toLowerCase();
  return [
    l.idBd, l.lote, l.modeloTramitado, l.modeloReservado, l.cliente,
    l.nomBloque, l.nomCreadorApc, l.estadoActual, l.banco, l.vendedor
  ].some(v => v && v.toLowerCase().includes(n));
}

// Las etapas (Diseño / APC CFIA / APC Institucional / APC Municipal /
// APC EN) no viven en FasesARQ — se derivan de los estados (cada
// EstadoLoteARQ tiene su etapa + su fase). En el seed actual cada fase
// pertenece a una sola etapa, asi que tomamos la etapa del primer estado.
// Si una fase no tiene estados, la dejamos sin etiqueta de etapa (cae en
// un grupo "Otros" cuyo header queda vacio).
function buildEtapaPorFase(estados: EstadoLote[]): Map<number, string> {
  const map = new Map<number, string>();
  for (const e of estados) {
    if (!map.has(e.idFase)) map.set(e.idFase, e.etapa);
  }
  return map;
}

// Agrupa fases CONSECUTIVAS que comparten etapa. Asume el seed actual:
// el orden de fases respeta la secuencia de etapas. Si en el futuro se
// rompe esa contigüidad, simplemente apareceran headers repetidos —
// no es un crash, es solo "menos lindo".
interface EtapaGroup {
  etapa: string;
  fases: Fase[];
}

function groupFasesByEtapa(fases: Fase[], etapaPorFase: Map<number, string>): EtapaGroup[] {
  const out: EtapaGroup[] = [];
  for (const f of fases) {
    const etapa = etapaPorFase.get(f.idFase) ?? '';
    const last = out[out.length - 1];
    if (last && last.etapa === etapa) {
      last.fases.push(f);
    } else {
      out.push({ etapa, fases: [f] });
    }
  }
  return out;
}

// Estado del picker que aparece tras soltar un lote sobre una columna.
interface PickerState {
  lote: LoteKanban;
  fase: Fase;
  x: number;
  y: number;
}

export function LotesKanbanBoard({ proyecto, search = '', creadorApcId = null }: Props) {
  const { data: fases = [], isLoading: loadingFases } = useFases();
  const { data: estados = [] } = useEstadosLote();
  const { data: lotes = [], isLoading: loadingLotes, error } = useLotesKanban(proyecto);
  const changeEstado = useChangeEstadoLote(proyecto);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } })
  );

  const [activeLote, setActiveLote] = useState<LoteKanban | null>(null);
  const [picker, setPicker] = useState<PickerState | null>(null);

  const filtered = useMemo(() => {
    let out = lotes;
    if (creadorApcId != null) {
      out = out.filter(l => l.idCreadorApc === creadorApcId);
    }
    if (search) {
      out = out.filter(l => matchSearch(l, search));
    }
    return out;
  }, [lotes, search, creadorApcId]);
  const grouped = useMemo(() => agrupar(filtered, fases), [filtered, fases]);
  const orderedFases = useMemo(
    () => [...fases].sort((a, b) => a.orden - b.orden),
    [fases]
  );
  // Solo consideramos los estados del tipo de proyecto activo. Como
  // consecuencia las fases sin estados de ese tipo (ej: "Revision EN")
  // tampoco aparecen en visibleFases.
  const estadosActivos = useMemo(
    () => estados.filter(e => e.tipoProyecto === TIPO_PROYECTO_ACTIVO),
    [estados]
  );
  const etapaPorFase = useMemo(() => buildEtapaPorFase(estadosActivos), [estadosActivos]);
  const visibleFases = useMemo(
    () => orderedFases.filter(f => etapaPorFase.has(f.idFase)),
    [orderedFases, etapaPorFase]
  );
  const etapaGroups = useMemo(
    () => groupFasesByEtapa(visibleFases, etapaPorFase),
    [visibleFases, etapaPorFase]
  );

  function handleDragStart(e: DragStartEvent) {
    const lote = e.active.data.current?.lote as LoteKanban | undefined;
    if (lote) setActiveLote(lote);
  }

  function handleDragEnd(e: DragEndEvent) {
    const lote = e.active.data.current?.lote as LoteKanban | undefined;
    setActiveLote(null);
    if (!lote || !e.over) return;

    const targetFaseId = e.over.data.current?.faseId as number | undefined;
    if (typeof targetFaseId !== 'number') return;
    const targetFase = fases.find(f => f.idFase === targetFaseId);
    if (!targetFase) return;

    // Coordenadas del cursor al soltar: ubicacion sugerida del picker.
    // dnd-kit no expone directamente el pointer event; usamos el rect del
    // droppable como ancla (esquina superior izquierda + un pequeño offset).
    const rect = e.over.rect;
    const x = rect ? rect.left + 16 : window.innerWidth / 2 - 160;
    const y = rect ? rect.top + 48 : window.innerHeight / 2 - 180;

    setPicker({ lote, fase: targetFase, x, y });
  }

  function handlePick(estado: EstadoLote) {
    if (!picker) return;
    const { lote } = picker;
    setPicker(null);
    // El picker ya filtra a estados de la fase elegida, asi que confiamos
    // en estado.idFase como nuevo idFaseLote del optimistic update.
    changeEstado.mutate({
      idLote: lote.idLote,
      idEstadoLote: estado.idEstadoLote,
      idFaseLote: estado.idFase,
    });
  }

  if (loadingFases || loadingLotes) {
    return <div className="p-8 text-center text-stone-500">Cargando…</div>;
  }
  if (error) {
    return <div className="p-8 text-center text-red-600">Error al cargar lotes.</div>;
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="flex gap-3 p-3 overflow-x-auto h-full items-stretch">
        {etapaGroups.map((group, i) => (
          <div
            key={`${group.etapa}-${i}`}
            className="flex flex-col gap-1.5 flex-shrink-0 min-h-0"
          >
            {/* Header de etapa: envuelve las columnas de fase que le pertenecen.
                Cada etapa tiene su familia de color (ver ETAPA_COLORS arriba).
                Las columnas debajo gradientean hacia el verde "Permiso de
                Construccion" — objetivo final del flujo. */}
            <div
              className="px-3 py-1.5 rounded text-[11px] font-semibold uppercase tracking-wide text-center text-white"
              style={{ background: etapaColor(group.etapa) }}
            >
              {group.etapa || '—'}
            </div>
            <div className="flex gap-2 min-h-0 flex-1">
              {group.fases.map(f => (
                <LotesKanbanColumn
                  key={f.idFase}
                  faseId={f.idFase}
                  faseNombre={f.nombre}
                  faseColor={f.colorHex || '#888780'}
                  lotes={grouped.get(f.idFase) ?? []}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      <DragOverlay>
        {activeLote ? <LoteCard lote={activeLote} overlay /> : null}
      </DragOverlay>

      {picker && (
        <EstadoPickerDialog
          lote={picker.lote}
          fase={picker.fase}
          x={picker.x}
          y={picker.y}
          tipoProyecto={TIPO_PROYECTO_ACTIVO}
          onPick={handlePick}
          onClose={() => setPicker(null)}
        />
      )}
    </DndContext>
  );
}
